import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { reportsAPI } from '../services/api';
import { formatDateTimeEST } from '../utils/dateUtils';

interface ReportData {
  event: {
    uuid: string;
    title: string;
    description: string;
    event_address: string;
    event_start_date: string;
    event_end_date: string;
    organizer_name: string;
    ticket_price_adults: number;
    ticket_price_kids: number;
  };
  ticketSales: {
    totalSold: number;
    adultTickets: number;
    kidTickets: number;
    scanned: number;
    pending: number;
  };
  registrations: {
    total: number;
    totalAdults: number;
    totalKids: number;
    confirmed: number;
    ticketsEmailed: number;
    complimentaryCount: number;
    complimentaryTickets: number;
  };
  income: {
    ticketSales: {
      adult: number;
      kid: number;
      baseTotal: number;
      discountApplied: number;
      total: number;
    };
    otherIncome: number;
    totalRevenue: number;
  };
  expenses: {
    total: number;
    breakdown: any;
  };
  transactions: Array<{
    uuid: string;
    transaction_type: string;
    amount: number;
    description: string;
    transaction_date: string;
    payment_mode: string;
  }>;
  summary: {
    totalRevenue: number;
    totalExpenses: number;
    netProfit: number;
    profitMargin: string;
  };
}

const EventReport: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState<ReportData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (uuid) {
      fetchReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid]);

  // Auto-dismiss error after 10 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await reportsAPI.getEventReport(uuid!);
      setReport(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return formatDateTimeEST(dateString);
  };

  const formatCurrency = (amount: number) => {
    return `CAD $${amount.toFixed(2)}`;
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-600 mx-auto mb-4"></div>
          <p className="text-primary-700 text-xl font-semibold">Loading report...</p>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="flex-1 flex items-center justify-center p-4 bg-gradient-to-br from-primary-50 to-primary-100">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-red-600 mb-4">Error Loading Report</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/dashboard?context=events')}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition"
          >
            Back to Events
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-gray-50">
      <div className="container mx-auto px-4 py-8 print:px-0">
        {/* Header Actions - Hidden on Print */}
        <div className="flex justify-between items-center mb-6 print:hidden">
          <button
            onClick={() => navigate('/dashboard?context=events')}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition"
          >
            ← Back to Events
          </button>
          <button
            onClick={handlePrint}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print Report
          </button>
        </div>

        {/* Report Content */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Event Header */}
          <div className="border-b-2 border-gray-200 pb-6 mb-6">
            <h1 className="text-4xl font-bold text-primary-900 mb-2">Income & Expenditure Report</h1>
            <h2 className="text-2xl text-gray-700 mb-4">{report.event.title}</h2>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
              <div>
                <p><strong>Organizer:</strong> {report.event.organizer_name}</p>
                <p><strong>Location:</strong> {report.event.event_address}</p>
              </div>
              <div>
                <p><strong>Start:</strong> {formatDate(report.event.event_start_date)}</p>
                <p><strong>End:</strong> {formatDate(report.event.event_end_date)}</p>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-lg">
              <p className="text-sm text-green-700 font-semibold mb-1">Total Revenue</p>
              <p className="text-2xl font-bold text-green-900">{formatCurrency(report.summary.totalRevenue)}</p>
            </div>
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
              <p className="text-sm text-red-700 font-semibold mb-1">Total Expenses</p>
              <p className="text-2xl font-bold text-red-900">{formatCurrency(report.summary.totalExpenses)}</p>
            </div>
            <div className={`${report.summary.netProfit >= 0 ? 'bg-blue-50 border-blue-500' : 'bg-orange-50 border-orange-500'} border-l-4 p-4 rounded-lg`}>
              <p className={`text-sm font-semibold mb-1 ${report.summary.netProfit >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                Net {report.summary.netProfit >= 0 ? 'Profit' : 'Loss'}
              </p>
              <p className={`text-2xl font-bold ${report.summary.netProfit >= 0 ? 'text-blue-900' : 'text-orange-900'}`}>
                {formatCurrency(Math.abs(report.summary.netProfit))}
              </p>
            </div>
            <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded-lg">
              <p className="text-sm text-purple-700 font-semibold mb-1">Profit Margin</p>
              <p className="text-2xl font-bold text-purple-900">{report.summary.profitMargin}%</p>
            </div>
          </div>

          {/* Ticket Sales & Registration */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Ticket Sales</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Adult Tickets Sold:</span>
                  <span className="font-semibold">{report.ticketSales.adultTickets}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Kid Tickets Sold:</span>
                  <span className="font-semibold">{report.ticketSales.kidTickets}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-gray-900 font-semibold">Total Tickets:</span>
                  <span className="font-bold">{report.ticketSales.totalSold}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-600">Scanned:</span>
                  <span className="font-semibold text-green-600">{report.ticketSales.scanned}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-yellow-600">Pending:</span>
                  <span className="font-semibold text-yellow-600">{report.ticketSales.pending}</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Registrations</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Registrations:</span>
                  <span className="font-semibold">{report.registrations.total}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Confirmed:</span>
                  <span className="font-semibold">{report.registrations.confirmed}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tickets Emailed:</span>
                  <span className="font-semibold">{report.registrations.ticketsEmailed}</span>
                </div>
                {report.registrations.complimentaryTickets > 0 && (
                  <div className="flex justify-between">
                    <span className="text-purple-600">Complimentary Tickets:</span>
                    <span className="font-semibold text-purple-600">{report.registrations.complimentaryTickets}</span>
                  </div>
                )}
                <div className="flex justify-between border-t pt-2">
                  <span className="text-gray-600">Total Adults:</span>
                  <span className="font-semibold">{report.registrations.totalAdults}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Kids:</span>
                  <span className="font-semibold">{report.registrations.totalKids}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Income Breakdown */}
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Income Breakdown</h3>
            <div className="bg-green-50 p-6 rounded-lg">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Adult Ticket Sales ({report.ticketSales.adultTickets} × {formatCurrency(report.event.ticket_price_adults)}):</span>
                  <span className="font-semibold text-green-700">{formatCurrency(report.income.ticketSales.adult)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Kid Ticket Sales ({report.ticketSales.kidTickets} × {formatCurrency(report.event.ticket_price_kids)}):</span>
                  <span className="font-semibold text-green-700">{formatCurrency(report.income.ticketSales.kid)}</span>
                </div>
                <div className="flex justify-between items-center border-t border-green-200 pt-2">
                  <span className="text-gray-900">Subtotal (Before Discount):</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(report.income.ticketSales.baseTotal)}</span>
                </div>
                {report.income.ticketSales.discountApplied > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-orange-700">Discount Applied:</span>
                    <span className="font-semibold text-orange-700">-{formatCurrency(report.income.ticketSales.discountApplied)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center border-t border-green-200 pt-2">
                  <span className="text-gray-900 font-semibold">Total Ticket Sales:</span>
                  <span className="font-bold text-green-900">{formatCurrency(report.income.ticketSales.total)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Other Income:</span>
                  <span className="font-semibold text-green-700">{formatCurrency(report.income.otherIncome)}</span>
                </div>
                <div className="flex justify-between items-center border-t-2 border-green-300 pt-3 mt-2">
                  <span className="text-xl font-bold text-gray-900">Total Revenue:</span>
                  <span className="text-xl font-bold text-green-900">{formatCurrency(report.income.totalRevenue)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Expenses */}
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Expenses</h3>
            <div className="bg-red-50 p-6 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-xl font-bold text-gray-900">Total Expenses:</span>
                <span className="text-xl font-bold text-red-900">{formatCurrency(report.expenses.total)}</span>
              </div>
            </div>
          </div>

          {/* Transactions Table */}
          {report.transactions.length > 0 && (
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Transaction Details</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-4 py-2 text-left">Date</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Type</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">From/To</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Description</th>
                      <th className="border border-gray-300 px-4 py-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.transactions.map((tx) => (
                      <tr key={tx.uuid} className="hover:bg-gray-50">
                        <td className="border border-gray-300 px-4 py-2">{formatDate(tx.transaction_date)}</td>
                        <td className="border border-gray-300 px-4 py-2">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            tx.transaction_type === 'income' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {tx.transaction_type?.toUpperCase() || 'N/A'}
                          </span>
                        </td>
                        <td className="border border-gray-300 px-4 py-2">{tx.payment_mode || 'N/A'}</td>
                        <td className="border border-gray-300 px-4 py-2">{tx.description || 'N/A'}</td>
                        <td className={`border border-gray-300 px-4 py-2 text-right font-semibold ${
                          tx.transaction_type === 'income' ? 'text-green-700' : 'text-red-700'
                        }`}>
                          {tx.transaction_type === 'income' ? '+' : '-'}{formatCurrency(tx.amount || 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="border-t-2 border-gray-200 pt-6 text-center text-gray-500 text-sm">
            <p>Report Generated: {formatDate(new Date().toISOString())}</p>
            <p className="mt-2">© SoundX724 - संगीत का संसार - The World of Music</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventReport;
